import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { localeActual, useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { VerMasAlBajar } from './VerMasAlBajar.js';

/**
 * The pieces every tab of «Análisis» is drawn with (`routes/Analisis.tsx` and the tabs and sections
 * of their own, `ui/Analisis*.tsx`): a section, a list of ten with the rest a tap away, a row, the
 * names that open their page, the dates as the page writes them, and the sheet that lists what a
 * figure adds up.
 */

/** How many rows a list shows, the rest a tap away under it (`Corta`). */
export const CUANTOS = 10;

/** A percentage to a tenth, and whole from 1000% up, as the arrows write it (`Cambio`). */
export const pct = (bp: number | null): string => (bp === null ? '—' : `${(bp / 100).toFixed(Math.abs(bp) >= 100_000 ? 0 : 1)}%`);
/** A share of a whole, in basis points; null with no whole. */
export const parte = (de: number, total: number): number | null => (total > 0 ? Math.round((de * 10_000) / total) : null);
export const comoFecha = (fecha: string): Date => new Date(`${fecha}T00:00:00Z`);

export function useFechas() {
  const locale = localeActual();
  return useMemo(() => {
    const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { ...o, timeZone: 'UTC' });
    const corta = fmt({ day: 'numeric', month: 'short' });
    const conDia = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
    const mesCorto = fmt({ month: 'short' });
    const mesLargo = fmt({ month: 'long', year: 'numeric' });
    // 1 February 2026 was a Sunday, and Nicaragua's weeks start on one.
    const semana = (weekday: 'short' | 'long') =>
      Array.from({ length: 7 }, (_, i) => fmt({ weekday }).format(new Date(Date.UTC(2026, 1, 1 + i))));
    return {
      corta: (f: string) => corta.format(comoFecha(f)),
      conDia: (f: string) => conDia.format(comoFecha(f)),
      mesCorto: (f: string) => mesCorto.format(comoFecha(f)),
      mesLargo: (f: string) => mesLargo.format(comoFecha(f)),
      diasCortos: semana('short'),
      diasLargos: semana('long'),
    };
  }, [locale]);
}

export function Cargando() {
  const t = useT();
  return <p className="py-12 text-center text-sm text-slate-500">{t('analisis.cargando')}</p>;
}

export function Seccion({ titulo, ayuda, children, datos, accion }: { titulo: string; ayuda?: string; children: ReactNode; datos: string; accion?: ReactNode }) {
  return (
    <section className="tarjeta" data-seccion={datos}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0">
          <h2 className="font-semibold">{titulo}</h2>
          {ayuda && <p className="mt-0.5 text-xs text-slate-500">{ayuda}</p>}
        </div>
        {accion}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function Nada({ texto }: { texto: string }) {
  return <p className="py-4 text-center text-sm text-slate-500">{texto}</p>;
}

/**
 * A list of ten, and «Ver 23 más» under it for the rest: it said «Y 23 más.» with no way to them
 * (the owner, 2026-10-12, of a list that stopped short). `children` draws the rows to show.
 */
export function Corta<T>({ filas, children }: { filas: readonly T[]; children: (vistas: readonly T[]) => ReactNode }) {
  const t = useT();
  const [entera, setEntera] = useState(false);
  const resto = entera ? 0 : filas.length - CUANTOS;
  return (
    <>
      {children(resto > 0 ? filas.slice(0, CUANTOS) : filas)}
      {resto > 0 && (
        <button type="button" className="btn-texto mt-3 gap-1 text-sm text-slate-400" onClick={() => setEntera(true)} data-analisis-ver-mas>
          <Ico icono="desplegar" className="size-4" />
          {t('analisis.verMas', { n: resto })}
        </button>
      )}
    </>
  );
}

/** A row of a list: a name that opens its page, what it is under it, a figure on the right. */
export function Fila({ a, nombre, detalle, valor, rojo = false }: { a: ReactNode; nombre?: string; detalle: string; valor: string; rojo?: boolean }) {
  return (
    <li className="flex items-start justify-between gap-3 py-2.5 text-sm" data-fila={nombre}>
      <div className="min-w-0">
        <div className="wrap-break-word">{a}</div>
        <p className="text-xs text-slate-500">{detalle}</p>
      </div>
      <span className={`tabular shrink-0 ${rojo ? 'text-rose-400' : ''}`}>{valor}</span>
    </li>
  );
}

export function AProducto({ id, nombre }: { id: string; nombre: string }) {
  return (
    <Link to="/productos/$productoId" params={{ productoId: id }} data-sin-icono="fila" className="hover:underline">
      {nombre}
    </Link>
  );
}

export function ACliente({ id, nombre }: { id: string; nombre: string }) {
  return (
    <Link to="/clientes/$clienteId" params={{ clienteId: id }} data-sin-icono="fila" className="hover:underline">
      {nombre}
    </Link>
  );
}

/** A record a figure adds up, as the sheet lists it. */
export interface Renglon {
  clave: string;
  /** What it is: a sale's ticket and who bought, an entry's concept. */
  titulo: ReactNode;
  /** Under it: the day, and what else tells it apart. */
  detalle: string;
  /** Its part of the figure, already written. */
  valor: string;
  rojo?: boolean;
}

/** Fifty at a time: a year of sales is thousands of rows, and a cheap phone draws them all at once. */
const RENGLONES_A_LA_VEZ = 50;

/**
 * What a figure adds up, in a sheet (the owner, 2026-10-13: «tap any figure to see the records
 * behind it»): its records, the newest first, fifty at a time and more as the sheet is scrolled.
 * `resumen` says what they come to, which is the figure tapped.
 */
export function DetalleDeCifra({
  titulo,
  resumen,
  renglones,
  vacio,
  pie,
  datos,
  onCerrar,
}: {
  titulo: string;
  resumen?: string;
  /** Null while they are being read. */
  renglones: readonly Renglon[] | null;
  vacio: string;
  pie?: ReactNode;
  datos: string;
  onCerrar: () => void;
}) {
  const [cuantos, setCuantos] = useState(RENGLONES_A_LA_VEZ);
  return (
    <Modal titulo={titulo} onCerrar={onCerrar} pie={pie}>
      <div data-detalle-cifra={datos}>
        {resumen && (
          <p className="mb-2 text-sm text-slate-300" data-detalle-resumen>
            {resumen}
          </p>
        )}
        {renglones === null ? (
          <Cargando />
        ) : renglones.length === 0 ? (
          <Nada texto={vacio} />
        ) : (
          <ul className="divide-y divide-slate-800">
            {renglones.slice(0, cuantos).map((r) => (
              <li key={r.clave} className="flex items-start justify-between gap-3 py-2.5 text-sm" data-renglon={r.clave}>
                <div className="min-w-0">
                  <div className="wrap-break-word">{r.titulo}</div>
                  <p className="text-xs text-slate-500">{r.detalle}</p>
                </div>
                <span className={`tabular shrink-0 ${r.rojo ? 'text-rose-400' : ''}`}>{r.valor}</span>
              </li>
            ))}
          </ul>
        )}
        {renglones !== null && renglones.length > cuantos && (
          <VerMasAlBajar quedan={renglones.length - cuantos} onMas={() => setCuantos((n) => n + RENGLONES_A_LA_VEZ)} />
        )}
      </div>
    </Modal>
  );
}
